import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AgendaEvento } from '@eleg360/types';
import { agendaApi } from '@/lib/api/agenda';
import { categoriasApi } from '@/lib/api/core';
import { contatosApi } from '@/lib/api/contatos';
import { useAuthStore } from '@/lib/stores/authStore';
import { useBairros } from '@/lib/hooks/useBairros';
import { toast } from '@/lib/stores/toastStore';
import { Drawer } from '@/design-system/Drawer';
import { GradeSemanal, segundaDaSemana } from './GradeSemanal';
import { MotorConvite } from './MotorConvite';

const TIPOS = ['Plenária', 'Reunião', 'Evento Externo', 'Visita Técnica', 'Entrevista', 'Audiência'];

/**
 * Início da semana (segunda) da data informada — a mesma âncora da grade
 * (proto:672: a grade é de segunda a sexta), para que a navegação de semana e
 * a grade nunca fiquem defasadas de um dia.
 */
const inicioDaSemana = segundaDaSemana;

const mesmaData = (a: string, b: Date) => {
  const d = new Date(a);
  return (
    d.getFullYear() === b.getFullYear() &&
    d.getMonth() === b.getMonth() &&
    d.getDate() === b.getDate()
  );
};

const hora = (iso: string) =>
  new Date(iso).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });

type FormEvento = {
  titulo: string;
  tipo: string;
  data: string;
  horaInicio: string;
  horaFim: string;
  bairro: string;
  endereco: string;
  publico_alvo: string;
  publico: boolean;
  convites: string[];
};

const agora = new Date();
const FORM_VAZIO: FormEvento = {
  titulo: '',
  tipo: 'Reunião',
  data: agora.toISOString().slice(0, 10),
  horaInicio: '09:00',
  horaFim: '11:00',
  bairro: '',
  endereco: '',
  publico_alvo: '',
  publico: true,
  convites: [],
};

export function AgendaPage() {
  const token = useAuthStore((s) => s.token) || '';
  const queryClient = useQueryClient();
  const { bairros } = useBairros();

  const [semanaBase, setSemanaBase] = useState(() => inicioDaSemana(new Date()));
  const [selecionado, setSelecionado] = useState<AgendaEvento | null>(null);
  const [modal, setModal] = useState<'novo' | 'editar' | null>(null);
  const [form, setForm] = useState<FormEvento>(FORM_VAZIO);

  const erroDe = (err: any, padrao: string) => {
    const msg = err?.response?.data?.message;
    return Array.isArray(msg) ? msg.join(' · ') : msg || padrao;
  };

  const { data: eventos = [], isLoading } = useQuery({
    queryKey: ['agenda'],
    queryFn: () => agendaApi.list(token),
    enabled: !!token,
  });

  const { data: categorias = [] } = useQuery({
    queryKey: ['categorias', 'agenda'],
    queryFn: () => categoriasApi.list(token, 'agenda'),
    enabled: !!token,
  });

  const { data: contatosPagina } = useQuery({
    queryKey: ['contatos', 'agenda'],
    queryFn: () => contatosApi.list({ limit: 300 }, token),
    enabled: !!token,
  });
  const contatos = contatosPagina?.items ?? [];

  const invalidar = () => {
    queryClient.invalidateQueries({ queryKey: ['agenda'] });
    queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    queryClient.invalidateQueries({ queryKey: ['notificacoes'] });
  };

  const criar = useMutation({
    mutationFn: (dados: any) => agendaApi.create(dados, token),
    onSuccess: () => {
      invalidar();
      toast('Compromisso criado.');
      setModal(null);
    },
    onError: (err: any) => toast(erroDe(err, 'Erro ao criar o compromisso.')),
  });

  const atualizar = useMutation({
    mutationFn: ({ id, dados }: { id: string; dados: Partial<AgendaEvento> }) =>
      agendaApi.update(id, dados, token),
    onSuccess: (e) => {
      invalidar();
      toast('Compromisso atualizado.');
      setSelecionado(e);
      setModal(null);
    },
    onError: (err: any) => toast(erroDe(err, 'Erro ao atualizar o compromisso.')),
  });

  /**
   * Adia o compromisso em N dias, preservando a duracao.
   *
   * Nao ha endpoint proprio: o PUT da agenda ja recalcula o choque de horario
   * ao salvar, entao adiar reaproveita essa verificacao em vez de duplica-la.
   */
  const adiar = (dias: number) => {
    if (!selecionado) return;
    const desloca = (iso: string) => {
      const d = new Date(iso);
      d.setDate(d.getDate() + dias);
      return d.toISOString();
    };
    atualizar.mutate({
      id: selecionado.id,
      dados: { inicio: desloca(selecionado.inicio), fim: desloca(selecionado.fim) },
    });
  };

  const excluir = useMutation({
    mutationFn: (id: string) => agendaApi.delete(id, token),
    onSuccess: () => {
      invalidar();
      toast('Compromisso removido.');
      setSelecionado(null);
    },
    onError: (err: any) => toast(erroDe(err, 'Erro ao remover o compromisso.')),
  });

  /**
   * Sete dias, de segunda a domingo. A grade desenha só segunda a sexta, mas
   * `GradeSemanal` lista o que cai no fim de semana logo abaixo dela ("Fora da
   * grade de segunda a sexta"): contar 5 dias aqui faria o KPI relatar menos
   * compromissos do que a própria página mostra. O rótulo é que precisava
   * dizer qual é o intervalo — sem isso, o número não batia com a grade e não
   * havia como saber por quê.
   */
  const dias = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(semanaBase);
    d.setDate(d.getDate() + i);
    return d;
  });

  const doDia = (d: Date) =>
    eventos
      .filter((e) => mesmaData(e.inicio, d))
      .sort((a, b) => new Date(a.inicio).getTime() - new Date(b.inicio).getTime());

  const hojeMs = new Date().setHours(0, 0, 0, 0);
  const proximos = eventos
    .filter((e) => new Date(e.inicio).getTime() >= hojeMs)
    .sort((a, b) => new Date(a.inicio).getTime() - new Date(b.inicio).getTime());

  /**
   * `hora` chega do duplo clique numa célula da grade (proto:673,
   * `ondblclick="openDrawer('novo-comp')"`): o compromisso já nasce na faixa
   * em que o usuário clicou, em vez de sempre às 09:00.
   */
  const abrirNovo = (data?: Date, hora?: number) => {
    const dia = data ?? new Date();
    // `toISOString` converteria para UTC e poderia jogar o dia para trás.
    const iso = `${dia.getFullYear()}-${String(dia.getMonth() + 1).padStart(2, '0')}-${String(
      dia.getDate(),
    ).padStart(2, '0')}`;
    const hh = (h: number) => `${String(h).padStart(2, '0')}:00`;
    setForm({
      ...FORM_VAZIO,
      bairro: bairros[0]?.n || '',
      data: iso,
      horaInicio: hora === undefined ? FORM_VAZIO.horaInicio : hh(hora),
      horaFim: hora === undefined ? FORM_VAZIO.horaFim : hh(Math.min(23, hora + 2)),
    });
    setModal('novo');
  };

  const abrirEdicao = (e: AgendaEvento) => {
    const ini = new Date(e.inicio);
    setForm({
      titulo: e.titulo,
      tipo: e.tipo,
      data: ini.toISOString().slice(0, 10),
      horaInicio: hora(e.inicio),
      horaFim: hora(e.fim),
      bairro: e.bairro || '',
      endereco: e.endereco || '',
      publico_alvo: e.publico_alvo || '',
      publico: e.publico,
      convites: e.convites || [],
    });
    setSelecionado(e);
    setModal('editar');
  };

  const submeter = (ev: React.FormEvent) => {
    ev.preventDefault();
    if (!form.titulo.trim()) {
      toast('O título do compromisso é obrigatório.');
      return;
    }
    const inicio = new Date(`${form.data}T${form.horaInicio}:00`);
    const fim = new Date(`${form.data}T${form.horaFim}:00`);
    if (fim <= inicio) {
      toast('O horário de término precisa ser depois do início.');
      return;
    }

    const dados = {
      titulo: form.titulo.trim(),
      tipo: form.tipo,
      inicio: inicio.toISOString(),
      fim: fim.toISOString(),
      bairro: form.bairro || undefined,
      municipio: form.bairro ? 'Fortaleza' : undefined,
      uf: form.bairro ? 'CE' : undefined,
      endereco: form.endereco.trim() || undefined,
      publico_alvo: form.publico_alvo.trim() || undefined,
      publico: form.publico,
      convites: form.convites,
    };

    if (modal === 'novo') criar.mutate(dados);
    else if (selecionado) atualizar.mutate({ id: selecionado.id, dados });
  };

  const moverSemana = (delta: number) => {
    const d = new Date(semanaBase);
    d.setDate(d.getDate() + delta * 7);
    setSemanaBase(d);
  };

  const rotuloSemana = `${dias[0]!.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })} a ${dias[6]!.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}`;

  return (
    <div className="page" id="p-agenda">
      <div className="head">
        <div>
          <h1>Agenda</h1>
          <p>
            Compromissos do mandato com território, público-alvo e convidados. Eventos marcados como
            públicos podem ser divulgados externamente.
          </p>
        </div>
        <div className="act">
          <button className="btn pri" onClick={() => abrirNovo()}>
            + Novo compromisso
          </button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid g4">
        <div className="card kpi">
          <div className="l">Compromissos futuros</div>
          <div className="v">{proximos.length}</div>
          <div className="d">de {eventos.length} na agenda</div>
        </div>
        <div className="card kpi">
          <div className="l">Nesta semana</div>
          <div className="v" style={{ color: 'var(--accent)' }}>
            {dias.reduce((t, d) => t + doDia(d).length, 0)}
          </div>
          <div className="d">{rotuloSemana} · seg a dom, inclui o fim de semana</div>
        </div>
        <div className="card kpi">
          <div className="l">Públicos</div>
          <div className="v">{eventos.filter((e) => e.publico).length}</div>
          <div className="d">divulgáveis no site oficial</div>
        </div>
        <div className="card kpi">
          <div className="l">Territórios na agenda</div>
          <div className="v">{new Set(eventos.map((e) => e.bairro).filter(Boolean)).size}</div>
          <div className="d">de {bairros.length} do mandato</div>
        </div>
      </div>

      {/* Navegação da semana */}
      <div className="filters">
        <button className="btn sm" onClick={() => moverSemana(-1)}>
          ‹ Semana anterior
        </button>
        <b style={{ fontSize: 13 }}>{rotuloSemana}</b>
        <button className="btn sm" onClick={() => moverSemana(1)}>
          Próxima semana ›
        </button>
        <button className="btn sm" onClick={() => setSemanaBase(inicioDaSemana(new Date()))}>
          Hoje
        </button>
      </div>

      {isLoading && <div className="empty">Carregando a agenda...</div>}

      {/* Semana — grade hora x dia (proto:672-673) */}
      {!isLoading && (
        <div style={{ marginTop: 12 }}>
          <GradeSemanal
            eventos={eventos}
            semana={semanaBase}
            aoAbrirEvento={setSelecionado}
            aoNovoCompromisso={(dia, h) => abrirNovo(dia, h)}
          />
        </div>
      )}

      {/* Próximos */}
      <div className="card" style={{ marginTop: 12 }}>
        <h2>Próximos compromissos</h2>
        <div className="tw" style={{ marginTop: 8 }}>
          <table>
            <thead>
              <tr>
                <th>Quando</th>
                <th>Compromisso</th>
                <th>Tipo</th>
                <th>Local</th>
                <th>Convidados</th>
                <th>Visibilidade</th>
              </tr>
            </thead>
            <tbody>
              {proximos.slice(0, 12).map((e) => (
                <tr key={e.id} className="row" style={{ cursor: 'pointer' }} onClick={() => setSelecionado(e)}>
                  <td className="mono" style={{ whiteSpace: 'nowrap', fontSize: 12 }}>
                    {new Date(e.inicio).toLocaleDateString('pt-BR')} {hora(e.inicio)}
                  </td>
                  <td>
                    <b>{e.titulo}</b>
                  </td>
                  <td>
                    <span className="chip neutral">{e.tipo}</span>
                  </td>
                  <td>
                    {e.bairro || '—'}
                    {e.endereco && (
                      <div style={{ fontSize: 11, color: 'var(--ink3)' }}>{e.endereco}</div>
                    )}
                  </td>
                  <td className="num">{(e.convites || []).length}</td>
                  <td>
                    <span className={`chip ${e.publico ? 'good' : 'warn'}`}>
                      {e.publico ? 'Público' : 'Interno'}
                    </span>
                  </td>
                </tr>
              ))}
              {proximos.length === 0 && !isLoading && (
                <tr>
                  <td colSpan={6} className="empty">
                    Nenhum compromisso futuro na agenda.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Drawer: detalhe */}
      {selecionado && !modal && (
        <Drawer
          aberto
          titulo={selecionado.titulo}
          aoFechar={() => setSelecionado(null)}
        >
          <div className="filters" style={{ marginBottom: 12 }}>
            <span className="chip neutral">{selecionado.tipo}</span>
            {selecionado.bairro && <span className="chip eleg">{selecionado.bairro}</span>}
            <span className={`chip ${selecionado.publico ? 'good' : 'warn'}`}>
              {selecionado.publico ? 'Público' : 'Interno'}
            </span>
          </div>

          <dl className="dl">
            <dt>Quando</dt>
            <dd>
              {new Date(selecionado.inicio).toLocaleDateString('pt-BR')} ·{' '}
              {hora(selecionado.inicio)} às {hora(selecionado.fim)}
            </dd>
            <dt>Local</dt>
            <dd>{selecionado.endereco || selecionado.bairro || '—'}</dd>
            <dt>Público-alvo</dt>
            <dd>{selecionado.publico_alvo || '—'}</dd>
            <dt>Convidados</dt>
            <dd>{(selecionado.convites || []).length} contato(s)</dd>
            <dt>Confirmados</dt>
            <dd>{(selecionado.confirmacoes || []).length}</dd>
          </dl>

          <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
            <button
              className="btn sm"
              onClick={() => adiar(1)}
              disabled={atualizar.isPending}
              title="Move o compromisso um dia para frente, mantendo o horário"
            >
              Adiar 1 dia
            </button>
            <button
              className="btn sm"
              onClick={() => adiar(7)}
              disabled={atualizar.isPending}
              title="Move o compromisso uma semana para frente"
            >
              Adiar 1 semana
            </button>
          </div>

          <MotorConvite evento={selecionado} />

          {(selecionado.convites || []).length > 0 && (
            <>
              <h3 style={{ marginTop: 14 }}>Lista de convidados</h3>
              <div style={{ maxHeight: 200, overflowY: 'auto', marginTop: 6 }}>
                {(selecionado.convites || []).map((cid) => {
                  const c = contatos.find((x) => x.id === cid);
                  return (
                    <div
                      key={cid}
                      style={{
                        fontSize: 12,
                        padding: '5px 0',
                        borderBottom: '1px solid var(--line)',
                      }}
                    >
                      {c?.nome || cid}
                      {c?.bairro && (
                        <span style={{ color: 'var(--ink3)' }}> · {c.bairro}</span>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}

          <div style={{ display: 'flex', gap: 8, marginTop: 18 }}>
            <button className="btn" onClick={() => abrirEdicao(selecionado)}>
              ✏️ Editar
            </button>
            <button
              className="btn"
              disabled={atualizar.isPending}
              onClick={() =>
                atualizar.mutate({
                  id: selecionado.id,
                  dados: { publico: !selecionado.publico },
                })
              }
            >
              Tornar {selecionado.publico ? 'interno' : 'público'}
            </button>
            <button
              className="btn"
              style={{ color: 'var(--crit)', marginLeft: 'auto' }}
              disabled={excluir.isPending}
              onClick={() => {
                if (window.confirm(`Remover "${selecionado.titulo}" da agenda?`)) {
                  excluir.mutate(selecionado.id);
                }
              }}
            >
              Excluir
            </button>
          </div>
        </Drawer>
      )}

      {/* Modal: novo / editar */}
      {modal && (
        <form onSubmit={submeter}>
          <Drawer
            aberto
            titulo={modal === 'novo' ? 'Novo compromisso' : 'Editar compromisso'}
            aoFechar={() => setModal(null)}
            rodape={
              <>
                <button type="button" className="btn" onClick={() => setModal(null)}>
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="btn pri"
                  disabled={criar.isPending || atualizar.isPending}
                >
                  {criar.isPending || atualizar.isPending
                    ? 'Salvando...'
                    : modal === 'novo'
                    ? 'Criar compromisso'
                    : 'Salvar alterações'}
                </button>
              </>
            }
          >
            <div className="field">
              <label>Título *</label>
              <input
                className="in"
                value={form.titulo}
                onChange={(e) => setForm({ ...form, titulo: e.target.value })}
                placeholder="Ex: Reunião com lideranças de Messejana"
                required
              />
            </div>

            <div className="grid g2">
              <div className="field">
                <label>Tipo</label>
                <input
                  className="in"
                  list="tipos-agenda"
                  value={form.tipo}
                  onChange={(e) => setForm({ ...form, tipo: e.target.value })}
                />
                <datalist id="tipos-agenda">
                  {[...TIPOS, ...categorias.map((c) => c.nome)].map((t) => (
                    <option key={t} value={t} />
                  ))}
                </datalist>
              </div>
              <div className="field">
                <label>Data</label>
                <input
                  type="date"
                  className="in"
                  value={form.data}
                  onChange={(e) => setForm({ ...form, data: e.target.value })}
                />
              </div>
            </div>

            <div className="grid g2">
              <div className="field">
                <label>Início</label>
                <input
                  type="time"
                  className="in"
                  value={form.horaInicio}
                  onChange={(e) => setForm({ ...form, horaInicio: e.target.value })}
                />
              </div>
              <div className="field">
                <label>Término</label>
                <input
                  type="time"
                  className="in"
                  value={form.horaFim}
                  onChange={(e) => setForm({ ...form, horaFim: e.target.value })}
                />
              </div>
            </div>

            <div className="grid g2">
              <div className="field">
                <label>Território</label>
                <select
                  className="in"
                  value={form.bairro}
                  onChange={(e) => setForm({ ...form, bairro: e.target.value })}
                >
                  <option value="">Não informado</option>
                  {bairros.map((b) => (
                    <option key={b.id} value={b.n}>
                      {b.n}
                    </option>
                  ))}
                </select>
              </div>
              <div className="field">
                <label>Visibilidade</label>
                <select
                  className="in"
                  value={form.publico ? '1' : '0'}
                  onChange={(e) => setForm({ ...form, publico: e.target.value === '1' })}
                >
                  <option value="1">Público · divulgável</option>
                  <option value="0">Interno</option>
                </select>
              </div>
            </div>

            <div className="field">
              <label>Endereço</label>
              <input
                className="in"
                value={form.endereco}
                onChange={(e) => setForm({ ...form, endereco: e.target.value })}
                placeholder="Local do compromisso"
              />
            </div>

            <div className="field">
              <label>Público-alvo</label>
              <input
                className="in"
                value={form.publico_alvo}
                onChange={(e) => setForm({ ...form, publico_alvo: e.target.value })}
                placeholder="Ex: Moradores e lideranças comunitárias"
              />
            </div>

            <div className="field">
              <label>Convidados ({form.convites.length})</label>
              <div
                style={{
                  maxHeight: 160,
                  overflowY: 'auto',
                  border: '1px solid var(--line)',
                  borderRadius: 6,
                  padding: 8,
                }}
              >
                {contatos.slice(0, 100).map((c) => (
                  <label
                    key={c.id}
                    style={{ display: 'flex', gap: 8, fontSize: 12, padding: '3px 0', cursor: 'pointer' }}
                  >
                    <input
                      type="checkbox"
                      checked={form.convites.includes(c.id)}
                      onChange={() =>
                        setForm({
                          ...form,
                          convites: form.convites.includes(c.id)
                            ? form.convites.filter((x) => x !== c.id)
                            : [...form.convites, c.id],
                        })
                      }
                    />
                    {c.nome}
                    {c.bairro && <span style={{ color: 'var(--ink3)' }}>· {c.bairro}</span>}
                  </label>
                ))}
                {contatos.length === 0 && (
                  <div style={{ fontSize: 12, color: 'var(--ink3)' }}>
                    Nenhum contato cadastrado para convidar.
                  </div>
                )}
              </div>
            </div>

          </Drawer>
        </form>
      )}
    </div>
  );
}
